import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { IMaterial } from '@interfaces/IMaterial';
import { select, Store } from '@ngrx/store';
import { IAppState } from '@myStore/app.state';
import { UntypedFormControl } from '@angular/forms';
import { UIThemeService } from '@services/UITheme/UITheme.service';
import { fromEvent, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { selectChangesEventLoading } from '@myStore/events-changes/events-changes.selector';

@Component({
  selector: 'app-search-field-autocomplete',
  templateUrl: './search-field-autocomplete.component.html',
  styleUrls: ['./search-field-autocomplete.component.sass'],
})
export class SearchFieldAutocompleteComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  @ViewChild('input', { static: true }) input!: ElementRef;
  @Output() searchHandler = new EventEmitter<string | null>();
  @Output() selectHandler = new EventEmitter<number | null>();
  @Output() inputOutFocus = new EventEmitter<null>();
  @Input() isDisabled?: boolean;
  @Input() helperText?: string;
  @Input() autoComplete?: boolean;
  @Input() materials$?: IMaterial[];
  @Input() data?: { name: string; id: number }[];
  @Input() loading$?: boolean;
  @Input() label?: string;

  private prevInputLength = 0;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  public materialsControl: UntypedFormControl = new UntypedFormControl('');
  public addEventMaterialLoading$ = false;

  constructor(
    private _store: Store<IAppState>,
    public UIThemeService: UIThemeService
  ) {}

  ngAfterViewInit(): void {
    fromEvent(this.input.nativeElement, 'keyup')
      .pipe(
        map(e => {
          const event = e as Event;
          const target = event.target as HTMLInputElement;

          return target.value;
        }),
        debounceTime(400),
        distinctUntilChanged(),
        tap((value: string) => {
          this.search(value);
        })
      )
      .subscribe();
  }

  ngOnInit() {
    this._store
      .pipe(select(selectChangesEventLoading), takeUntil(this.destroy$))
      .subscribe(loading => {
        if (loading) {
          this.addEventMaterialLoading$ = loading;
          this.materialsControl.disable();
        } else {
          this.materialsControl.enable();
        }
      });
  }

  search(value: string): void {
    if (value.length > 2) {
      this.prevInputLength = 2;
      this.searchHandler.emit(value);
    }

    if (this.prevInputLength === 2 && value.length <= 1) {
      this.prevInputLength = 0;
      this.searchHandler.emit(null);
    }
  }

  select(id: number): void {
    this.selectHandler.emit(id);

    if (!this.data) {
      this.input.nativeElement.value = '';
    }
  }

  isEmptyData(): boolean {
    return Boolean(!this.loading$ && this.data && this.data.length === 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  inputInFocus() {
    this.inputOutFocus.emit();
  }
}
